import {
  ActivityStats,
  Challenge,
  Event,
  EventService,
  Geogroup,
  GeogroupNewsPost,
  GeogroupService,
  HttpService,
  LockOutlinedIcon,
  Period,
  Ride,
  RideService,
  ShareIcon,
  UserChallenge,
  UserGeogroup,
  UserService,
  currentMonth,
  prevMonth,
  useCancellablePromise,
} from '@geovelo-frontends/commons';
import { Add } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link, navigate } from 'gatsby';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import defaultBanner from '../../assets/images/community_default_banner.svg';
import Button from '../../components/button';
import ConfirmDialog from '../../components/confirm-dialog';
import {
  CommunitiesIcon,
  EditIcon,
  MoreVertIcon,
  RemoveIcon,
  SignOutIcon,
} from '../../components/icons';
import Seo from '../../components/seo';
import ShareDialog from '../../components/share-dialog';
import { maxWidth } from '../../components/slices/consts';
import { AppContext } from '../../context';
import environment from '../../environment';
import useQueryParams from '../../hooks/query-params';
import { TPageProps } from '../../page-props';
import Breadcrumb from '../communities/breadcrumb';
import PartnerTerritory from '../ride-details/partner-territory';

import Challenges from './challenges';
import News from './news';
import NewsFormDialog from './news-form-dialog';
import Profile from './profile';
import Rides from './rides';
import Stats from './stats';

type TTab = 'profile' | 'rides' | 'news' | 'stats' | 'challenges';

function CommunityPage(props: TPageProps): JSX.Element {
  const [geoGroupId] = useState(parseInt(props.params.id));
  const [initialized, setInitialized] = useState(false);
  const [geogroup, setGeogroup] = useState<Geogroup>();
  const [userGeogroup, setUserGeogroup] = useState<UserGeogroup>();
  const { get: getQueryParams, update: updateQueryParams } = useQueryParams(props.location.search);
  const [tab, setTab] = useState<TTab>(() => {
    const tab = getQueryParams().tab;
    if (tab === 'news') return 'news';
    if (tab === 'rides') return 'rides';
    if (tab === 'stats') return 'stats';
    if (tab === 'challenges') return 'challenges';

    return 'profile';
  });
  const [rides, setRides] = useState<Ride[]>();
  const [newsPosts, setNewsPosts] = useState<GeogroupNewsPost[]>();
  const [challenges, setChallenges] = useState<Challenge[]>();
  const [userChallenges, setUserChallenges] = useState<UserChallenge[]>();
  const [joiningChallenge, setJoiningChallenge] = useState<{ [key: number]: boolean }>({});
  const [highlightedEventChallenge, setHighlightedEventChallenge] = useState<Challenge | null>();
  const [activities, setActivities] = useState<ActivityStats[]>();
  const [invitationLink, setInvitationLink] = useState<string>();
  const [invitationCode, setInvitationCode] = useState<string>();
  const [highlightedEvent, setHighlightedEvent] = useState<Event | null>();
  const [shareDialogOpen, openShareDialog] = useState(false);
  const [newsPostToUpdate, setNewsPostToUpdate] = useState<GeogroupNewsPost>();
  const [newsPostFormDialogOpen, openNewsPostFormDialog] = useState(false);
  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [leaveDialogOpen, openLeaveDialog] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [removeDialogOpen, openRemoveDialog] = useState(false);
  const [removing, setRemoving] = useState(false);
  const {
    user: { current: currentUser, geogroups: userGeogroups },
    actions: { setHasHeaderDarkBackground, setHeaderBackgroundColor, getUserGeogroups },
  } = useContext(AppContext);
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const { transitions } = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { cancellablePromise, cancelPromises } = useCancellablePromise();
  const { cancellablePromise: cancellableNewsPostPromise, cancelPromises: cancelNewsPostPromises } =
    useCancellablePromise();
  const {
    cancellablePromise: cancellableUserChallengesPromise,
    cancelPromises: cancelUserChallengesPromises,
  } = useCancellablePromise();
  const {
    cancellablePromise: cancellableJoinChallengePromise,
    cancelPromises: cancelJoinChallengePromise,
  } = useCancellablePromise();
  const { cancellablePromise: cancellableLinkPromise, cancelPromises: cancelLinkPromise } =
    useCancellablePromise();
  const {
    cancellablePromise: cancellableAvailableEventsPromise,
    cancelPromises: cancelAvailableEventsPromise,
  } = useCancellablePromise();

  useEffect(() => {
    setHasHeaderDarkBackground(true);
    setHeaderBackgroundColor('#528be8');
    setInitialized(true);

    return () => {
      setHasHeaderDarkBackground(false);
      setHeaderBackgroundColor();
    };
  }, []);

  useEffect(() => {
    if (initialized) getGeogroup();

    return () => {
      cancelPromises();
    };
  }, [initialized]);

  useEffect(() => {
    if (initialized && currentUser && !userGeogroups) getUserGeogroups();
  }, [initialized, currentUser]);

  useEffect(() => {
    if (initialized) updateQueryParams({ tab });
  }, [initialized, tab]);

  useEffect(() => {
    if (geogroup) {
      getGeogroupNews(geogroup.id);
      getChallenges(geogroup.id);
      getActivities(geogroup.id);

      if (environment.highlightedEventIds && environment.highlightedEventIds.length > 0)
        getAvailableEvents(environment.highlightedEventIds, geogroup);
      else setHighlightedEvent(null);

      if (geogroup.type === 'city' && geogroup.partner) getRides(geogroup.partner.id);

      if (geogroup.bannerPicture) setHeaderBackgroundColor('rgba(0, 0, 0, 0.5)');
    }

    return () => {
      cancelPromises();
      cancelNewsPostPromises();
      cancelUserChallengesPromises();
      cancelJoinChallengePromise();
      cancelAvailableEventsPromise();
    };
  }, [geogroup]);

  useEffect(() => {
    if (geogroup && userGeogroups) {
      const userGeogroup = userGeogroups.find(
        ({ status, group: { id } }) =>
          id === geogroup.id && (status === 'automaticallyJoin' || status === 'manuallyJoin'),
      );

      if (userGeogroup) {
        setUserGeogroup(userGeogroup);
        getLink(geogroup.id);
      } else if (geogroup.type === 'city') {
        joinGeogroup(geogroup);
      } else {
        navigate(`/${currentLanguage}/communities`);
      }
    }

    return () => cancelLinkPromise();
  }, [geogroup, userGeogroups]);

  useEffect(() => {
    if (geogroup && currentUser && challenges) {
      if (challenges.length > 0) getUserChallenges(geogroup.id, challenges);
      else setUserChallenges([]);
    } else if (currentUser === null) {
      setUserChallenges([]);
    }

    return () => cancelUserChallengesPromises();
  }, [geogroup, currentUser, challenges]);

  async function getGeogroup() {
    try {
      const result = await cancellablePromise(GeogroupService.getGeogroup({ geoGroupId }));
      if (result.type === 'company') {
        console.error('geogroup is a company community');
        window.open(
          `${environment.enterpriseUrl}?auth-token=${
            HttpService.authorizationToken?.substring(
              HttpService.authorizationToken.indexOf(' ') + 1,
            ) || ''
          }&user-id=${currentUser?.id || ''}&partner-id=${result.partner?.id || ''}`,
          '_self',
        );
      } else {
        setGeogroup(result);
      }
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('geovelo.communities.server_error'), { variant: 'error' });
        navigate(`/${currentLanguage}/communities`);
      }
    }
  }

  async function joinGeogroup(geogroup: Geogroup) {
    try {
      const userGeogroup = await cancellablePromise(GeogroupService.joinGeogroup(geogroup.id));

      setUserGeogroup(userGeogroup);
      getLink(geogroup.id);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
        navigate(`/${currentLanguage}/communities`);
      }
    }
  }

  async function getActivities(id: number) {
    setActivities(undefined);

    try {
      const _activities = await cancellablePromise(
        Promise.all([
          GeogroupService.getActivityStats({ geoGroupId: id }, currentMonth.toIPeriod()),
          GeogroupService.getActivityStats({ geoGroupId: id }, prevMonth.toIPeriod()),
        ]),
      );

      setActivities(_activities);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('geovelo.communities.activities.server_error'), { variant: 'error' });
      }
    }
  }

  async function getRides(partnerId: number) {
    try {
      const { rides } = await cancellablePromise(
        RideService.getRides({
          ordering: '-id',
          page: 1,
          pageSize: 3,
          partnerIds: [partnerId],
          query:
            '{id, title, geo_point_a, geo_point_a_title, geo_point_b, geo_point_b_title, duration, route_duration, distance, vertical_gain, difficulty, photos{thumbnail, thumbnailSquare}, themes, partners{id, title, code, icon}}',
        }),
      );

      setRides(rides);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('geovelo.rides.server_error'), { variant: 'error' });
      }
    }
  }

  async function getGeogroupNews(geogroupId: number) {
    try {
      cancelNewsPostPromises();
      setNewsPosts(undefined);

      const { results } = await cancellableNewsPostPromise(
        GeogroupService.getNewsPosts(geogroupId, { ordering: '-created', page: 1, pageSize: 3 }),
      );

      setNewsPosts(results);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('commons.news.server_error'), { variant: 'error' });
      }
    }
  }

  async function getChallenges(geogroupId: number) {
    try {
      const { challenges } = await cancellablePromise(
        GeogroupService.getChallenges(geogroupId, {
          page: 1,
          pageSize: 3,
          state: ['running', 'future'],
          order: '-start_datetime',
        }),
      );

      setChallenges(challenges);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('geovelo.communities.challenges.server_error'), {
          variant: 'error',
        });
        setChallenges(undefined);
      }
    }
  }

  async function getAvailableEvents(eventIds: number[], geogroup: Geogroup) {
    try {
      const events = await cancellableAvailableEventsPromise(
        EventService.getAvailableEvents({
          geogroupId: geogroup.id,
          page: 1,
          pageSize: 100,
          state: ['running', 'future'],
          // automaticSubscription: 'DISABLED',
        }),
      );

      const highlightedEvent = events.find(({ id }) => eventIds.includes(id)) || null;

      if (highlightedEvent) getHighlightedEventChallenge(geogroup, highlightedEvent);

      setHighlightedEvent(highlightedEvent);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        setHighlightedEvent(null);
      }
    }
  }

  async function getUserChallenges(geogroupId: number, challenges: Challenge[]) {
    try {
      const userChallenges = await cancellableUserChallengesPromise(
        UserService.getUserChallenges({
          groupIds: [geogroupId],
          page: 1,
          pageSize: 100,
          query:
            '{leaderboard, progress_value, day_streak_details, challenge{id, target_type, trace_type}}',
          challengeIds: challenges.map(({ id }) => id),
        }),
      );

      setUserChallenges(userChallenges);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('geovelo.communities.challenges.server_error'), {
          variant: 'error',
        });
        setChallenges(undefined);
      }
    }
  }

  async function getHighlightedEventChallenge(geogroup: Geogroup, highlightedEvent: Event) {
    try {
      const { challenges } = await cancellableAvailableEventsPromise(
        GeogroupService.getChallenges(geogroup.id, {
          page: 1,
          pageSize: 50,
          period: new Period('custom', highlightedEvent.startDate, highlightedEvent.endDate),
          query: '{id, target_type, publication_status, event}',
        }),
      );

      setHighlightedEventChallenge(
        challenges?.find(({ eventId }) => eventId === highlightedEvent.id) || null,
      );
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
      }
    }
  }

  async function getLink(geogroupId: number) {
    try {
      const { code, link } = await cancellableLinkPromise(
        GeogroupService.getInvitationLink(geogroupId),
      );

      setInvitationCode(code);
      setInvitationLink(link);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('geovelo.communities.invitation_link_dialog.server_error'), {
          variant: 'error',
        });
      }
    }
  }

  function handleNewsPostFormDialogOpen(post?: GeogroupNewsPost) {
    setNewsPostToUpdate(post);
    openNewsPostFormDialog(true);
  }

  function handleNewsPostFormDialogClose(post?: GeogroupNewsPost) {
    if (post && newsPosts) {
      if (newsPostToUpdate) {
        const index = newsPosts.findIndex(({ id }) => id === newsPostToUpdate.id);
        if (index >= 0) {
          const updatedNewsPosts = [...newsPosts];
          updatedNewsPosts.splice(index, 1, post);
          setNewsPosts(updatedNewsPosts);
        }
      } else {
        const updatedNewsPosts = [post, ...newsPosts];
        setNewsPosts(updatedNewsPosts);
      }
    }

    openNewsPostFormDialog(false);
    setTimeout(() => setNewsPostToUpdate(undefined), transitions.duration.leavingScreen);
  }

  async function handleChallengeJoin(challenge: Challenge) {
    if (!geogroup) return;

    setJoiningChallenge({ ...joiningChallenge, [challenge.id]: true });

    try {
      await cancellableJoinChallengePromise(
        UserService.joinChallenge({ geogroupId: geogroup.id, challengeId: challenge.id }),
      );

      navigate(`/${currentLanguage}/communities/${geogroup.id}/challenges/${challenge.id}`);
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('geovelo.communities.challenges.server_error'), { variant: 'error' });
      }
    }

    setJoiningChallenge({ ...joiningChallenge, [challenge.id]: true });
  }

  async function handleLeaveDialogClose(confirmed: boolean) {
    if (confirmed && geogroup && userGeogroup && !userGeogroup.isAdmin) {
      setLeaving(true);

      try {
        await cancellablePromise(GeogroupService.leaveGeogroup(geogroup.id));

        getUserGeogroups();
        navigate(`/${currentLanguage}/communities`);
      } catch (err) {
        if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
          console.error(err);
        }
      }

      setLeaving(false);
    }

    openLeaveDialog(false);
  }

  async function handleRemoveDialogClose(confirmed: boolean) {
    if (confirmed && geogroup && userGeogroup && userGeogroup.isAdmin) {
      setRemoving(true);

      try {
        await cancellablePromise(GeogroupService.removeGeogroup(geogroup.id));

        getUserGeogroups();
        navigate(`/${currentLanguage}/communities`);
      } catch (err) {
        if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
          console.error(err);
        }
      }

      setRemoving(false);
    }

    openRemoveDialog(false);
  }

  return (
    <>
      <Seo title={geogroup ? geogroup.title : t('geovelo.pages_titles.community')} {...props} />
      <Box
        alignItems="center"
        alignSelf="stretch"
        display="flex"
        flexDirection="column"
        flexShrink={0}
        justifyContent="center"
        maxHeight={300}
        paddingBottom={6}
        paddingTop={2}
        position="relative"
        sx={{ aspectRatio: 2, backgroundColor: '#528be8' }}
      >
        <Box
          bottom={0}
          left={0}
          position="absolute"
          right={0}
          sx={{
            backgroundImage: `url(${geogroup?.bannerPicture || defaultBanner})`,
            backgroundPosition: geogroup?.bannerPicture ? 'center' : 'bottom center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
          top={-72}
          zIndex={0}
        />
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        gap={{ xs: 6, lg: 8 }}
        paddingBottom={{ xs: 6, lg: 8 }}
        zIndex={2}
      >
        <Box alignItems="center" alignSelf="stretch" display="flex" flexDirection="column">
          <Box
            display="flex"
            flexDirection="column"
            gap={5}
            maxWidth={{
              xs: 'calc(100% - 64px)',
              md: 'calc(100% - 128px)',
              lg: 'calc(100% - 256px)',
            }}
            width={maxWidth}
          >
            <Box
              display="flex"
              flexDirection={{ xs: 'column', md: 'row' }}
              gap={{ xs: 2, md: 5, lg: 8 }}
            >
              <Box flexShrink={0} width={{ md: 250 }}>
                <Box
                  alignSelf="flex-start"
                  bgcolor="#fff"
                  border="1px solid #C0C7CC"
                  borderRadius="50%"
                  height={{ xs: 96, md: 192 }}
                  marginTop={-5}
                  width={{ xs: 96, md: 192 }}
                >
                  {geogroup ? (
                    <Avatar
                      src={geogroup.photo || undefined}
                      sx={({ palette }) => ({
                        bgcolor: '#fff',
                        color: palette.primary.main,
                        fontSize: '2rem',
                        height: { xs: 94, md: 190 },
                        width: { xs: 94, md: 190 },
                      })}
                    >
                      {!geogroup.photo && geogroup.title.substring(0, 2)}
                    </Avatar>
                  ) : (
                    <Skeleton
                      sx={{ height: { xs: 94, md: 190 }, width: { xs: 94, md: 190 } }}
                      variant="circular"
                    />
                  )}
                </Box>
              </Box>
              <Box
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                flexGrow={1}
                gap={4}
                justifyContent="space-between"
                paddingBottom={1}
                paddingTop={3}
              >
                <Box display="flex" flexDirection="column" gap={2}>
                  <Breadcrumb
                    dark
                    geogroup={geogroup}
                    items={geogroup ? [{ label: geogroup.title }] : []}
                  />
                  <Box
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    display="flex"
                    flexDirection={{ xs: 'column', md: 'row' }}
                    flexGrow={1}
                    gap={{ xs: 1, md: 2 }}
                  >
                    <Typography component="h1" fontWeight={700} variant="h5">
                      {geogroup ? geogroup.title : <Skeleton variant="text" width={200} />}
                    </Typography>
                    <PartnerTerritory partner={geogroup?.partner} />
                  </Box>
                  <Box alignItems="center" display="flex" gap={2}>
                    {geogroup?.type !== 'city' && !geogroup?.openMembership && (
                      <Box alignItems="center" display="flex" gap={1}>
                        <LockOutlinedIcon color="primary" fontSize="small" />
                        <Typography>
                          {t('commons.communities.joined_communities.private')}
                        </Typography>
                      </Box>
                    )}
                    <Box alignItems="center" display="flex" gap={1}>
                      <CommunitiesIcon color="primary" />
                      <Typography>
                        <Trans
                          count={geogroup?.nbMembers || 0}
                          i18nKey="commons.communities.members"
                          values={{ count: geogroup?.nbMembers || 0 }}
                        />
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  alignItems="center"
                  alignSelf={{ xs: 'flex-end', sm: 'center' }}
                  display="flex"
                  flexShrink={0}
                  gap={2}
                >
                  {currentUser && (
                    <>
                      {userGeogroup ? (
                        <Button
                          color="primary"
                          disabled={!geogroup}
                          onClick={() => openShareDialog(true)}
                          startIcon={<ShareIcon />}
                          variant="outlined"
                        >
                          {t('commons.actions.share')}
                        </Button>
                      ) : (
                        <Button
                          color="primary"
                          disabled={!geogroup}
                          startIcon={<Add />}
                          variant="contained"
                        >
                          {t('commons.communities.actions.join')}
                        </Button>
                      )}
                    </>
                  )}
                  {geogroup &&
                    ((userGeogroup &&
                      (userGeogroup.status === 'manuallyJoin' ||
                        userGeogroup.status === 'automaticallyJoin')) ||
                      (!geogroup.partner && userGeogroup?.isAdmin)) && (
                      <>
                        <IconButton
                          aria-controls="community-actions-menu"
                          aria-haspopup="true"
                          onClick={({ currentTarget }) => setActionsMenuAnchorEl(currentTarget)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                        <Menu
                          keepMounted
                          anchorEl={actionsMenuAnchorEl}
                          id="community-actions-menu"
                          onClose={() => setActionsMenuAnchorEl(null)}
                          open={Boolean(actionsMenuAnchorEl)}
                        >
                          {userGeogroup &&
                            !userGeogroup.isAdmin &&
                            (userGeogroup.status === 'manuallyJoin' ||
                              userGeogroup.status === 'automaticallyJoin') && (
                              <MenuItem
                                dense
                                onClick={() => {
                                  setActionsMenuAnchorEl(null);
                                  openLeaveDialog(true);
                                }}
                              >
                                <ListItemIcon>
                                  <SignOutIcon />
                                </ListItemIcon>
                                <ListItemText primary={t('geovelo.communities.actions.leave')} />
                              </MenuItem>
                            )}
                          {!geogroup.partner && userGeogroup?.isAdmin && (
                            <MenuItem
                              dense
                              component={Link}
                              to={`/${currentLanguage}/communities/${geoGroupId}/update`}
                            >
                              <ListItemIcon>
                                <EditIcon />
                              </ListItemIcon>
                              <ListItemText primary={t('geovelo.communities.actions.update')} />
                            </MenuItem>
                          )}
                          {!geogroup.partner && userGeogroup?.isAdmin && (
                            <MenuItem
                              dense
                              onClick={() => {
                                setActionsMenuAnchorEl(null);
                                openRemoveDialog(true);
                              }}
                            >
                              <ListItemIcon>
                                <RemoveIcon color="error" />
                              </ListItemIcon>
                              <ListItemText primary={t('geovelo.communities.actions.remove')} />
                            </MenuItem>
                          )}
                        </Menu>
                      </>
                    )}
                </Box>
              </Box>
            </Box>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={{ xs: 5, lg: 8 }}>
              <Box flexShrink={0} width={{ md: 250 }}>
                <Card sx={{ borderRadius: 4 }} variant="outlined">
                  <CardContent
                    sx={{ '&&': { overflowY: { xs: 'auto', lg: 'none' }, paddingBottom: 2 } }}
                  >
                    <Box
                      alignItems="flex-start"
                      display="flex"
                      flexDirection={{ md: 'column' }}
                      gap={2}
                    >
                      <Button
                        color={tab === 'profile' ? 'primary' : 'inherit'}
                        onClick={() => setTab('profile')}
                        sx={{ flexShrink: 0 }}
                        variant="text"
                      >
                        {t('geovelo.communities.navigation.general')}
                      </Button>
                      <Button
                        color={tab === 'stats' ? 'primary' : 'inherit'}
                        onClick={() => setTab('stats')}
                        sx={{ flexShrink: 0 }}
                        variant="text"
                      >
                        {t('geovelo.navigation.stats')}
                      </Button>
                      {(!newsPosts || newsPosts.length > 0) && (
                        <Button
                          color={tab === 'news' ? 'primary' : 'inherit'}
                          onClick={() => setTab('news')}
                          sx={{ flexShrink: 0 }}
                          variant="text"
                        >
                          {t('geovelo.communities.news.title')}
                        </Button>
                      )}
                      <Button
                        color={tab === 'challenges' ? 'primary' : 'inherit'}
                        onClick={() => setTab('challenges')}
                        sx={{ flexShrink: 0 }}
                        variant="text"
                      >
                        {t('geovelo.communities.challenges.title')}
                      </Button>
                      {geogroup?.partner && (!rides || rides.length > 0) && (
                        <Button
                          color={tab === 'rides' ? 'primary' : 'inherit'}
                          onClick={() => setTab('rides')}
                          sx={{ flexShrink: 0 }}
                          variant="text"
                        >
                          {t('geovelo.navigation.rides')}
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                flexGrow={1}
                gap={8}
                sx={{ overflowX: 'auto' }}
              >
                {tab === 'profile' && (
                  <Profile
                    activities={activities}
                    challenges={challenges}
                    geogroup={geogroup}
                    handleChallengeJoin={handleChallengeJoin}
                    handleNewsPostFormDialogOpen={handleNewsPostFormDialogOpen}
                    handleNewsPostRemoved={() => geogroup && getGeogroupNews(geogroup.id)}
                    highlightedEvent={highlightedEvent}
                    highlightedEventChallenge={highlightedEventChallenge}
                    joiningChallenge={joiningChallenge}
                    newsPosts={newsPosts}
                    rides={rides}
                    setHighlightedEventChallenge={setHighlightedEventChallenge}
                    setTab={setTab}
                    userChallenges={userChallenges}
                    userGeogroup={userGeogroup}
                  />
                )}
                {tab === 'stats' && <Stats geogroup={geogroup} />}
                {tab === 'news' && (
                  <News geogroup={geogroup} setTab={setTab} userGeogroup={userGeogroup} />
                )}
                {tab === 'challenges' && (
                  <Challenges
                    geogroup={geogroup}
                    highlightedEvent={highlightedEvent}
                    highlightedEventChallenge={highlightedEventChallenge}
                    setHighlightedEventChallenge={setHighlightedEventChallenge}
                    userGeogroup={userGeogroup}
                  />
                )}
                {tab === 'rides' && <Rides geogroup={geogroup} setTab={setTab} />}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <ShareDialog
        disableShortLink
        code={invitationCode}
        onClose={() => openShareDialog(false)}
        open={shareDialogOpen}
        shareText={t('geovelo.communities.invitation_link_dialog.title') || ''}
        url={invitationLink}
      />
      <ConfirmDialog
        dangerous
        loading={leaving}
        onClose={handleLeaveDialogClose}
        open={leaveDialogOpen}
        title={t('geovelo.communities.leave_dialog_title')}
      />
      <ConfirmDialog
        dangerous
        loading={removing}
        onClose={handleRemoveDialogClose}
        open={removeDialogOpen}
        title={t('geovelo.communities.remove_dialog_title')}
      />
      <NewsFormDialog
        geogroup={geogroup}
        onClose={handleNewsPostFormDialogClose}
        open={newsPostFormDialogOpen}
        post={newsPostToUpdate}
      />
    </>
  );
}

export default CommunityPage;
